/**
 * @file Disco-daily JS constants.
 * @copyright Copyright (c) 2025-2026 Woolly Bear Software LLC,
 *            all rights reserved.
 */

import { viteEnv } from "./vite-env-vars";

const RUN_MODE = viteEnv.MODE ?? "development";

const API_URL = viteEnv.VITE_API_URL ?? "";
const IS_PUBLIC = viteEnv.VITE_IS_PUBLIC === "true";
const WEBSITE_URL = viteEnv.VITE_WEBSITE_URL ?? "";
const APP_VERSION = viteEnv.VITE_APP_VERSION ?? "";

// Submits straight to the Google Form's own endpoint (same form Feedback
// has always used) instead of iframing its UI, so we can style the form
// ourselves. Entry IDs come from the form's FB_PUBLIC_LOAD_DATA_ - if the
// form's questions are ever edited in the Forms UI, these may need re-sync.
const FEEDBACK_FORM_ACTION_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSclJnTe9MG9OLsvf5rNknNVInQEYthR52PJn8rYgRhHlUrObQ/formResponse";
const FEEDBACK_FORM_EMAIL_FIELD = "entry.1655222213";
const FEEDBACK_FORM_MESSAGE_FIELD = "entry.68620911";

const START_YEAR = 1910;
const CURR_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = Array.from(
  { length: CURR_YEAR - START_YEAR + 1 },
  (_, i) => START_YEAR + i,
).reverse();

const LIVE_PATTERNS = [
  /\blive\s+sessions\b/,
  /\blive\s+(at|in|from|on)\b/,
  /\blive\s+(version|recording|performance)\b/,
  /\blive\s+\d{4}/,
  /\btour\s+\d{4}\b/,
  /\bunplugged\b/,
  /\bbc\s+sessions\b/,
  /\bat the bbc\b/,
  /[-:(\[]\s*live\s*[-)\]]?/,
  /[-:(\[]\s*performance\s*[-)\]]?/,
  /\bperformance\b.*$/,
  /\bin concert\b/,
  /\blive concert\b/,
  /\blive bootleg\b/,
  /\bat the .+?\/.+?\d{4}/,
  /[-:]?\s*.+?\s\/\s.+?\s\/\s\d{4}/,
  /\brecorded at\b/,
];

const LIVE_ALBUM_PATTERNS = [
  /\blive['"]?\s*[^,]*,\s*\d{4}/,
  /\blive\s+(at|in|from|on)\b/,
  /\blive\s+(version|recording|performance)\b/,
  /\blive\s+\d{4}/,
  /\btour\s+\d{4}\b/,
  /\bunplugged\b/,
  /\bbc\s+sessions\b/,
  /\bat the bbc\b/,
  /[-:(\[]\s*live\s*[-)\]]?/,
  /[-:(\[]\s*performance\s*[-)\]]?/,
  /\bperformance\b.*$/,
  /\bin concert\b/,
  /\blive concert\b/,
  /\blive bootleg\b/,
  /\bat the .+?\/.+?\d{4}/,
  /[-:]?\s*.+?\s\/\s.+?\s\/\s\d{4}/,
];

const REMIX_AND_EDIT_PATTERNS = [
  /\bremix\b/,
  /\bmix\b/,
  /\bmixed\b/,
  /\brerecorded\b|re[- ]?recorded\b/,
  /\bsped[- ]?up\b/,
  /\bsped[- ]?down\b/,
  /\bslow(ed)?\s*(reverb)?\b/,
  /\brework\b/,
  /\bre[- ]?edit\b/,
  /\brad(io)?\s*edit\b/,
  /\bdub( version)?\b/,
  /\btv\s*track\b/,
  /[-:(\[]\s*(remix|mix|mixed|rerecorded|re[- ]?recorded|sped[- ]?(up|down)|slow(ed)?( reverb)?|faster|rework|re[- ]?edit|rad(io)?\s*edit|dub|tv\s*track)\s*[-)\]]?/,
];

const ACOUSTIC_AND_INSTRUMENTAL_PATTERNS = [
  /\bacoustic\b/,
  /\bunplugged\b/,
  /\bdub\s*version\b/,
  /\bdub\b/,
  /\binstrumental\b/,
  /\borchestral\b/,
  /\ba\s*cappella\b/,
  /\bkaraoke\b/,
  /[-:(\[]\s*(acoustic|unplugged|instrumental|a\s*cappella|karaoke|orchestral)\s*[-)\]]?/,
];

const DEMO_PATTERNS = [/\bdemo\b/, /\bdemos\b/, /[-:(\[]\s*demo(s)?\s*[-)\]]?/];

const INTROS_AND_SKITS_PATTERNS = [
  /\bintro\b/,
  /\boutro\b/,
  /\bskit\b/,
  /\binterlude\b/,
  /\b(album|album\s*version)?\s*(intro|outro|interlude)\b/,
  /[-:(\[]\s*(intro|outro|skit|interlude)\s*[-)\]]?/,
];

const REMASTER_SUFFIX_PATTERNS = [
  /\b\d{4}\s*(digital\s*)?remaster(ed)?\b/i,
  /\bremaster(ed)?\b/i,
  /\bnew mix\b/i,
  /\bmono\s+mix\b/i,
  /\bstereo\s+mix\b/i,
  /\boriginal\s+mix\b/i,
  /\bultimate\s+mix\b/i,
  /\bdefinitive\s+edition\b/i,
  /\bexpanded\s+edition\b/i,
  /\bspecial\s+edition\b/i,
  /\blimited\s+edition\b/i,
  /\bdeluxe\s+(version|edition)\b/i,
  /\bdemo\s+version\b/i,
  /\bsingle\s+version\b/i,
  /\balbum\s+version\b/i,
  /\bedit( version)?\b/i,
  /\bversion\b/i,
  /[-:]\s*(remaster(ed)?|mix|version|edition|reissue|demo|take)\b/i,
];

const FORMAT_SUFFIX_PATTERNS = [
  /\bep\b/i,
  /\blp\b/i,
  /\bbonus\s+track\b/i,
  /\bbonus\s+(version|edition|disc)\b/i,
  /\bsingle\s+(version|edit)?\b/i,
  /\bcompilation\b/i,
  /\bbox\s+set\b/i,
  /\banniversary\s+edition\b/i,
  /\bre[- ]?issue\b/i,
  /[-:]\s*(ep|lp|bonus( track)?|compilation|re[- ]?issue)\b/i,
  /\s*[-–]\s*overture$/i,
  /\s*[-–]\s*highlights$/i,
  /-\s*\d{4}\s*$/i,
];

export const HolidayMode = Object.freeze({
  HOLIDAY: "holiday",
  XMAS: "christmas",
  HANUKKAH: "hanukkah",
  KWANZAA: "kwanzaa",
});

const DEBUG = RUN_MODE !== "production";

console.log("Run Mode: ", RUN_MODE);
console.log("Is public?: ", IS_PUBLIC, typeof IS_PUBLIC);
console.log("Version: ", APP_VERSION);

export {
  RUN_MODE,
  API_URL,
  IS_PUBLIC,
  WEBSITE_URL,
  APP_VERSION,
  FEEDBACK_FORM_ACTION_URL,
  FEEDBACK_FORM_EMAIL_FIELD,
  FEEDBACK_FORM_MESSAGE_FIELD,
  AVAILABLE_YEARS,
  LIVE_PATTERNS,
  LIVE_ALBUM_PATTERNS,
  REMIX_AND_EDIT_PATTERNS,
  ACOUSTIC_AND_INSTRUMENTAL_PATTERNS,
  DEMO_PATTERNS,
  INTROS_AND_SKITS_PATTERNS,
  REMASTER_SUFFIX_PATTERNS,
  FORMAT_SUFFIX_PATTERNS,
  DEBUG,
};
